import * as _ from 'lodash-es';
import type React from 'react';
import { useCallback, useEffect, useId } from 'react';
import { useEffectEvent } from 'use-effect-event';

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const HOVER_DELAYS = { fast: 100, slow: 1000 } as const;
export type HoverDelay = keyof typeof HOVER_DELAYS;

type HoverState =
  | {
      state: 'entered';
      id: string;
      cleanups: {
        /**
         * Timer for mouse entered but stillness not detected
         */
        fallbackTimer: () => void;
        /**
         * Timer for mouse entered and stillness detected
         */
        stillnessTimer: () => void;
        mouseMoveListener: () => void;
      };
      hoverEndedAt: Date | null;
    }
  | {
      state: 'hovering';
      id: string;
      /**
       * The trigger for the hover state change.
       * Only 'mouseEnter' enables subsequent hover detection and has a shorter leave delay.
       */
      trigger: 'mouseEnter' | 'setHover';
      cleanups: { hideTimer: (() => void) | null };
    }
  | { state: 'idle'; hoverEndedAt: Date | null };

type Listener = (value: HoverState, previousValue: HoverState) => void;
const listenersByDelayGroup = new Map<string, readonly Listener[]>();
const hoverStateByDelayGroup = new Map<string, HoverState>();

const setUpTimer = (handler: () => void, delay: number) => {
  const timer = setTimeout(handler, delay);
  return () => clearTimeout(timer);
};

const getCleanups = <T extends object>(
  state: T & Partial<{ cleanups: Record<string, (() => void) | null> }>,
) => {
  if ('cleanups' in state && state.cleanups) {
    return Object.values(state.cleanups).filter((cleanup) => cleanup !== null);
  }
  return [];
};

const DEFAULT_HOVER_STATE = {
  state: 'idle',
  hoverEndedAt: null,
} as const satisfies HoverState;

const getHoverState = (hoverGroupId: string) =>
  hoverStateByDelayGroup.get(hoverGroupId) ?? DEFAULT_HOVER_STATE;

const setHoverState = (hoverGroupId: string, hoverState: HoverState) => {
  const previousHoverState = getHoverState(hoverGroupId);
  if (_.isEqual(previousHoverState, hoverState)) {
    return;
  }

  if (hoverState === DEFAULT_HOVER_STATE) {
    hoverStateByDelayGroup.delete(hoverGroupId);
  } else {
    hoverStateByDelayGroup.set(hoverGroupId, hoverState);
  }

  const cleanupFunctions = getCleanups(previousHoverState);
  const newCleanupFunctions = getCleanups(hoverState);

  cleanupFunctions.forEach((cleanup) => {
    if (!newCleanupFunctions.includes(cleanup)) {
      cleanup();
    }
  });

  const listeners = listenersByDelayGroup.get(hoverGroupId) ?? [];
  listeners.forEach((listener) => {
    listener(hoverState, previousHoverState);
  });
};

const subscribeHoverStateChange = (
  hoverGroupId: string,
  listener: Listener,
) => {
  listenersByDelayGroup.set(hoverGroupId, [
    ...(listenersByDelayGroup.get(hoverGroupId) ?? []),
    listener,
  ]);

  return () => {
    const listeners = listenersByDelayGroup.get(hoverGroupId) ?? [];
    const index = listeners.indexOf(listener);
    if (index >= 0) {
      if (listeners.length === 1) {
        listenersByDelayGroup.delete(hoverGroupId);
      } else {
        listenersByDelayGroup.set(hoverGroupId, [
          ...listeners.slice(0, index),
          ...listeners.slice(index + 1),
        ]);
      }
    }

    return !listenersByDelayGroup.has(hoverGroupId);
  };
};

const SUBSEQUENT_TRIGGER_ALLOWANCE = 2000;
const isSubsequentTrigger = (hoverState: HoverState) =>
  hoverState.state === 'hovering' ||
  (hoverState.state === 'idle' &&
    hoverState.hoverEndedAt &&
    Date.now() - hoverState.hoverEndedAt.valueOf() <=
      SUBSEQUENT_TRIGGER_ALLOWANCE);

const MOTION_DELAY = 1000;
const MOTION_DISTANCE = 10;

interface UseHoverIntentOptions {
  hoverDelay: HoverDelay;
  /**
   * Hover targets with the same delay group ID will trigger instant hover
   * when moving between them, if within the subsequent hover time window.
   * If not provided, subsequent hover will not be detected.
   */
  hoverGroupId?: string;
  onHoverChange: (
    hoveringId: string | null,
    previousHoveringId: string | null,
  ) => void;
}

interface UseHoverIntentResult {
  /**
   * Called when the mouse enters a hover target
   * @param id the ID of the hover target. It should be unique within the hover group.
   * @param event usually a mouse enter event.
   */
  onMouseEnter: (
    id: string,
    event: Pick<React.MouseEvent, 'target' | 'clientX' | 'clientY'>,
  ) => void;

  /**
   * Called when the mouse leaves a hover target
   * @param id the ID of the hover target. Same as the id passed to onMouseEnter.
   * If the id does not match the currently active hover target, this call will be ignored.
   */
  onMouseLeave: (id: string) => void;

  /**
   * Sets the current hovering ID directly. Unlike onMouseEnter and onMouseLeave,
   * this changes the hover state immediately, without waiting for the hover delay.
   * This is useful when you want to synchronize the hover state with an external state.
   * @param idOrSetter Either a new ID to set, or a function that takes the current ID and returns a new ID.
   * Pass null or return null from the function to clear the hover state for the hover group.
   * No action is taken if the new ID matches the current ID.
   */
  setHover: (
    idOrSetter: string | null | ((currentId: string | null) => string | null),
  ) => void;
}

function useHover({
  hoverDelay,
  onHoverChange,
  hoverGroupId: providedHoverGroupId,
}: UseHoverIntentOptions): UseHoverIntentResult {
  const localHoverGroupId = useId();
  const hoverGroupId = providedHoverGroupId ?? localHoverGroupId;
  const enterDelay = HOVER_DELAYS[hoverDelay];

  const onHoverChangeEvent = useEffectEvent(onHoverChange);
  useEffect(() => {
    const unsubscribe = subscribeHoverStateChange(
      hoverGroupId,
      (value, previousValue) => {
        const previousHoveringId =
          previousValue.state === 'hovering' ? previousValue.id : null;
        const hoveringId = value.state === 'hovering' ? value.id : null;
        if (previousHoveringId !== hoveringId) {
          onHoverChangeEvent(hoveringId, previousHoveringId);
        }
      },
    );

    return () => {
      const isEmpty = unsubscribe();
      if (isEmpty) setHoverState(hoverGroupId, DEFAULT_HOVER_STATE);
    };
  }, [hoverGroupId]);

  const onMouseEnter = useCallback<UseHoverIntentResult['onMouseEnter']>(
    (id, event) => {
      const startHovering = () => {
        setHoverState(hoverGroupId, {
          state: 'hovering',
          id,
          trigger: 'mouseEnter',
          cleanups: { hideTimer: null },
        });
      };

      const hoverState = getHoverState(hoverGroupId);
      if (hoverState.state === 'hovering') {
        if (hoverState.id === id) {
          setHoverState(hoverGroupId, {
            ...hoverState,
            cleanups: { hideTimer: null },
          });
        } else {
          startHovering();
        }
      } else if (isSubsequentTrigger(hoverState)) {
        startHovering();
      } else if (hoverState.state !== 'entered' || hoverState.id !== id) {
        let enteredPosition = { x: event.clientX, y: event.clientY };
        const listener = (originalEvent: Event) => {
          // Forcing the type due to a TypeScript limitation:
          // https://github.com/microsoft/TypeScript/issues/46819
          const { clientX, clientY } = originalEvent as MouseEvent;
          const dx = clientX - enteredPosition.x;
          const dy = clientY - enteredPosition.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance > MOTION_DISTANCE) {
            const currentHoverState = getHoverState(hoverGroupId);
            if (currentHoverState.state === 'entered') {
              setHoverState(hoverGroupId, {
                ...currentHoverState,
                cleanups: {
                  fallbackTimer: currentHoverState.cleanups.fallbackTimer,
                  stillnessTimer: setUpTimer(startHovering, enterDelay),
                  mouseMoveListener:
                    currentHoverState.cleanups.mouseMoveListener,
                },
              });
            }
            enteredPosition = { x: clientX, y: clientY };
          }
        };
        event.target.addEventListener('mousemove', listener);
        setHoverState(hoverGroupId, {
          state: 'entered',
          id,
          cleanups: {
            fallbackTimer: setUpTimer(startHovering, enterDelay + MOTION_DELAY),
            stillnessTimer: setUpTimer(startHovering, enterDelay),
            mouseMoveListener: () => {
              event.target.removeEventListener('mousemove', listener);
            },
          },
          hoverEndedAt: hoverState.hoverEndedAt,
        });
      }
    },
    [enterDelay, hoverGroupId],
  );

  const onMouseLeave = useCallback<UseHoverIntentResult['onMouseLeave']>(
    (id) => {
      const hoverState = getHoverState(hoverGroupId);
      if (hoverState.state === 'idle' || hoverState.id !== id) return;

      if (hoverState.state === 'entered') {
        setHoverState(hoverGroupId, {
          state: 'idle',
          hoverEndedAt: hoverState.hoverEndedAt,
        });
      } else {
        const leaveDelay =
          hoverState.trigger === 'mouseEnter' ? enterDelay / 2 : enterDelay;

        setHoverState(hoverGroupId, {
          ...hoverState,
          cleanups: {
            hideTimer: setUpTimer(() => {
              setHoverState(hoverGroupId, {
                state: 'idle',
                // Avoid subsequent trigger unless the hover is triggered by mouse event
                hoverEndedAt:
                  hoverState.trigger === 'mouseEnter' ? new Date() : null,
              });
            }, leaveDelay),
          },
        });
      }
    },
    [enterDelay, hoverGroupId],
  );

  const setHover = useCallback<UseHoverIntentResult['setHover']>(
    (idOrSetter) => {
      const hoverState = getHoverState(hoverGroupId);
      const currentId = hoverState.state === 'hovering' ? hoverState.id : null;
      const nextId =
        typeof idOrSetter === 'function' ? idOrSetter(currentId) : idOrSetter;
      if (nextId !== currentId) {
        setHoverState(
          hoverGroupId,
          nextId == null
            ? DEFAULT_HOVER_STATE
            : {
                state: 'hovering',
                id: nextId,
                trigger: 'setHover',
                cleanups: { hideTimer: null },
              },
        );
      }
    },
    [hoverGroupId],
  );

  return {
    onMouseEnter,
    onMouseLeave,
    setHover,
  };
}

export default useHover;
